/**
 * +world-resources: WoW-style loot toasts for world pickups (left centre, under the loot feed).
 * Each toast: a framed resource icon with a rarity glow, the name in its rarity colour and the
 * merged count ("+3 Fangsteel"). Repeats merge and pop; rare+ toasts get a sheen sweep and
 * linger longer; a hidden cache spotted nearby gets a notice. Every toast plays a soft chime
 * (meta/audio/sfx.ts) that brightens with rarity.
 *
 * Rows are pooled (TOAST_MAX); the per-frame update only expires rows and rewrites the DOM when
 * the model changed (no layout reads). Mounted from hud.ts with one tagged line.
 */
import type { SimEvent, UnitId } from '@keystone/sim';
import { el, setClass, setStyle, setText } from '../dom';
import { iconUrl } from '../icons';
import type { HudUpdate } from '../hud';
import type { HudWidget, HudWidgetDef } from '../widgets';
import { currencyGlyphSvg } from '../../meta/ui/glyphs';
import { emitSfx } from '../../meta/audio/sfx';
import { LOOT_RARITY_COLOR, type LootToast, LootToastModel, TOAST_MAX, toastText } from './lootModel';
import './loot.css';

interface Row {
  root: HTMLElement;
  icon: HTMLElement;
  img: HTMLImageElement;
  name: HTMLElement;
  toast: LootToast | null;
  version: number;
  iconKey: string | null | undefined;
  flip: boolean;
}

const CACHE_NOTE = 'Hidden cache nearby';

export class LootToasts implements HudWidget {
  readonly root: HTMLElement;
  readonly model = new LootToastModel();
  private readonly rows: Row[] = [];
  private level = 2;
  private dirty = false;
  /** Real-time clock of the latest update (ingest has only sim time). */
  private clock = 0;

  constructor(host: HTMLElement) {
    this.root = el('div', 'hk-ltoasts', host);
    this.root.setAttribute('aria-live', 'polite');
    for (let i = 0; i < TOAST_MAX; i++) {
      const root = el('div', 'hk-ltoast hk-hidden', this.root);
      const icon = el('span', 'hk-ltoast-icon', root);
      const img = el('img', '', icon);
      img.alt = '';
      img.decoding = 'async';
      const name = el('span', 'hk-ltoast-name', root);
      this.rows.push({ root, icon, img, name, toast: null, version: -1, iconKey: undefined, flip: false });
    }
  }

  ingest(events: readonly SimEvent[], _playerId: UnitId | null, _now: number): void {
    const now = this.clock || (typeof performance !== 'undefined' ? performance.now() : 0);
    for (const e of events) {
      if (e.type === 'pickup') {
        const t = this.model.push(e.resource, e.amount, this.level, now, e.rarity);
        this.dirty = true;
        emitSfx(`sfx.loot.${t.rarity}`);
      } else if (e.type === 'object_discovered') {
        this.model.note(CACHE_NOTE, 'rare', now);
        this.dirty = true;
        emitSfx('sfx.loot.discover');
      }
    }
  }

  update(h: HudUpdate, realNow: number): void {
    this.clock = realNow;
    this.level = h.world.mplus.level;
    if (this.model.expire(realNow)) this.dirty = true;
    if (!this.dirty) return;
    this.dirty = false;
    const es = this.model.entries;
    // Bottom-aligned: newest toast in the last row.
    const off = TOAST_MAX - es.length;
    for (let i = 0; i < TOAST_MAX; i++) {
      const r = this.rows[i]!;
      const t = i >= off ? es[i - off]! : null;
      setClass(r.root, 'hk-hidden', !t);
      if (!t) {
        r.toast = null;
        continue;
      }
      if (r.toast === t && r.version === t.version) continue;
      const fresh = r.toast !== t;
      r.toast = t;
      r.version = t.version;
      r.root.dataset['rarity'] = t.rarity;
      setStyle(r.root, '--lt-c', LOOT_RARITY_COLOR[t.rarity]);
      setClass(r.root, 'note', t.note);
      if (r.iconKey !== t.icon) {
        r.iconKey = t.icon;
        const url = t.icon ? iconUrl(t.icon, 64) : null;
        r.icon.querySelector('svg')?.remove();
        if (url) {
          r.img.src = url;
          r.img.style.display = '';
        } else {
          r.img.removeAttribute('src');
          r.img.style.display = 'none';
          r.icon.appendChild(currencyGlyphSvg(t.note ? 'rating' : 'gold'));
        }
      }
      setText(r.name, t.note ? t.label : toastText(t, t.amount));
      // Life animation (slide in, hold, fade) restarted on every change at the toast's age.
      const life = Math.max(1, t.until - t.at);
      setStyle(r.root, '--lt-life', `${life | 0}ms`);
      setStyle(r.root, 'animation-delay', `${-Math.min(life, Math.max(0, realNow - t.at)) | 0}ms`);
      r.flip = !r.flip;
      setClass(r.root, 'live-a', r.flip);
      setClass(r.root, 'live-b', !r.flip);
      setClass(r.root, 'merged', !fresh);
    }
  }

  dispose(): void {
    this.model.clear();
  }
}

/** Widget definition (hud.ts mounts it after the registered widgets). */
export const lootToastWidget: HudWidgetDef = {
  id: 'lootToasts',
  region: 'leftCenter',
  create: (host) => new LootToasts(host),
};
